import { SubmitEvent, useState } from 'react'
import styles from './PromoCode.module.scss'
import classNames from 'classnames'
import { useTranslation } from 'next-i18next/pages'
import { Button, TextField } from '@boltenergy-be/design-system'
import { CodeTypes } from 'types/promo-referral-codes'
import { PromoCodeProps } from 'components/promo-code/types'
import { InputGroupKeys, SimulationFieldKeys } from 'store/customer/enums'
import { requestAndSavePropositions } from 'features/simulation/utils'
import { updateInput } from 'store/customer/slice'
import { useStoreDispatch, useStoreSelector } from 'hooks/store'
import useSelectInputGroup from 'hooks/useSelectInputGroup'
import axios from 'axios'
import { setPromoOrReferralCodeInStore } from 'utils/customerFlow'
import { CheckPromoReferralCodeResponse, NextApiResponseBody } from 'types/bolt-api/request'
import PromoCodeTermsDrawer from 'components/terms-and-conditions-drawers/promo-code-terms/PromoCodeTermsDrawer'
import ReferralCodeTermsDrawer from 'components/terms-and-conditions-drawers/referral-code-terms/ReferralCodeTermsDrawer'
import SimulationRegistrationSideCardLayout from 'components/simulation-registration-side-card-layout/SimulationRegistrationSideCardLayout'
import PromoCodeSuccess from 'components/promo-code/PromoCodeSuccess'
import { trackEvent } from 'utils/tracking'

const PromoCode = ({ withDividers, className, variant }: PromoCodeProps) => {
  // Redux store
  const { promoCode, referralCode } = useSelectInputGroup(InputGroupKeys.SIMULATION)
  const { confirmationFromPortalRegistration } = useStoreSelector((store) => store.marketing)
  const promoReferralCode = referralCode || promoCode
  const dispatch = useStoreDispatch()

  // Local state
  const [code, setCode] = useState<string>()
  const [isChecked, setIsChecked] = useState<boolean>(false)
  const [isLoading, setIsLoading] = useState<boolean>(false)
  const [isValid, setIsValid] = useState<boolean>(false)
  const [showCodeInputField, setShowCodeInputField] = useState<boolean>(false)
  const [termsDrawerOpen, setTermsDrawerOpen] = useState<'promo' | 'referral' | false>(false)

  // i18n
  const { t } = useTranslation('common')

  // Constants
  const shownInSideCard = variant === 'side-card'

  /**
   * Handles the form submit
   * Checks if the promo/referral code is valid and saves it in the store if so
   *
   * @param {FormEvent} e
   */
  const handleOnSubmit = async (e: SubmitEvent<HTMLFormElement>) => {
    e.preventDefault()

    // Update loading
    setIsLoading(true)

    // Track initiate promocode
    trackEvent({ category: 'simulation', object: 'promo', action: 'start' }, { code })

    // Check if promo code is valid
    const { data } = await axios.get<NextApiResponseBody<CheckPromoReferralCodeResponse>>(`/api/website/promo-referral-code?code=${code}`)

    if (!data) {
      setIsValid(false)
      setIsChecked(true)
      setIsLoading(false)
      return
    }

    const checkedPromoReferralCode = data.data!

    // Set isValid
    setIsValid(checkedPromoReferralCode.valid)

    // Save & resimulate if valid
    if (checkedPromoReferralCode.valid) {
      // Hide code input field
      setShowCodeInputField(false)

      // PROMO CODE
      if (checkedPromoReferralCode.type === CodeTypes.PROMO && !!checkedPromoReferralCode.promoCode) {
        // Save promo code in store
        dispatch(
          updateInput({
            group: InputGroupKeys.SIMULATION,
            key: SimulationFieldKeys.PROMO_CODE,
            value: checkedPromoReferralCode.promoCode
          })
        )
        trackEvent({ category: 'simulation', object: 'promo', action: 'finish' }, { code, type: 'promo' })
      }

      // REFERRAL CODE
      else if (checkedPromoReferralCode.type === CodeTypes.REFERRAL && !!checkedPromoReferralCode.referralCode) {
        // Save referral code in store
        dispatch(
          updateInput({
            group: InputGroupKeys.SIMULATION,
            key: SimulationFieldKeys.REFERRAL_CODE,
            value: checkedPromoReferralCode.referralCode
          })
        )
        trackEvent({ category: 'simulation', object: 'promo', action: 'finish' }, { code, type: 'referral' })

        // Fetch & save VIAVIA promo code in store
        await setPromoOrReferralCodeInStore('VIAVIA')
      }

      // Refetch the propositions
      await requestAndSavePropositions()
    }

    if (!isChecked) setIsChecked(true)
    setIsLoading(false)
  }

  if (confirmationFromPortalRegistration) return null

  if (!showCodeInputField && !promoReferralCode) {
    return (
      <>
        {withDividers && <hr className={styles.hr} />}
        <div className={classNames(styles['promo-line'], className)}>
          <p>{t('promoCode.initial')}</p>
          <SimulationRegistrationSideCardLayout.Action onClick={() => setShowCodeInputField(true)}>
            {t('promoCode.action')}
          </SimulationRegistrationSideCardLayout.Action>
        </div>
      </>
    )
  }

  if (showCodeInputField) {
    return (
      <>
        {withDividers && <hr className={styles.hr} />}
        <form className={styles['promo-code-form']} onSubmit={(e) => handleOnSubmit(e)}>
          <TextField
            label={t('promoCode.label')}
            hiddenLabel={shownInSideCard}
            type="text"
            value={code}
            onChange={(e) => {
              setCode(e.target.value)
              setIsValid(!!e.target.value)
            }}
            suffix={shownInSideCard ? { as: <Button size="small" loading={isLoading} trailingIcon="plus" type="submit" /> } : undefined}
            error={isChecked && !isValid && t('promoCode.invalid')}
          />

          {!shownInSideCard && (
            <Button variant="secondary" loading={isLoading} disabled={!isValid} isFullwidth type="submit" className="mt-300">
              {t('promoCode.submit')}
            </Button>
          )}
        </form>
      </>
    )
  }

  return (
    <>
      {promoReferralCode && (
        <>
          {withDividers && <hr className={styles.hr} />}
          <PromoCodeSuccess {...{ promoReferralCode, variant }} showTerms={setTermsDrawerOpen} />
        </>
      )}

      <PromoCodeTermsDrawer isOpen={termsDrawerOpen === 'promo'} onRequestClose={() => setTermsDrawerOpen(false)} code={promoCode?.code} />
      <ReferralCodeTermsDrawer
        isOpen={termsDrawerOpen === 'referral'}
        onRequestClose={() => setTermsDrawerOpen(false)}
        referralCode={referralCode}
      />
    </>
  )
}

export default PromoCode
